import { Link, NavLink, Outlet } from 'react-router-dom'
import { useAuth } from '@/lib/auth-context'
import { Button } from '@/components/ui/button'
import {
  LayoutDashboard,
  Megaphone,
  PlaySquare,
  Building2,
  BarChart3,
  Film,
  Handshake,
  LogOut,
} from 'lucide-react'

const navItems = [
  { to: '/advertiser', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/advertiser/sponsors', label: 'Sponsors', icon: Building2 },
  { to: '/advertiser/ads', label: 'Ad creatives', icon: PlaySquare },
  { to: '/advertiser/placements', label: 'Placements', icon: Film },
  { to: '/advertiser/deals', label: 'Deals', icon: Handshake },
  { to: '/advertiser/analytics', label: 'Analytics', icon: BarChart3 },
]

export function AdvertiserLayout() {
  const { user, logout } = useAuth()

  return (
    <div className="min-h-screen bg-black text-white flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-gray-900 bg-gradient-to-b from-black to-[#050309]">
        <div className="px-6 py-6 border-b border-gray-900">
          <Link to="/" className="flex items-center gap-2">
            <img src="/img/logo.png" alt="Binyuma TV" className="w-8 h-8 object-contain" />
            <div>
              <p className="text-sm font-semibold tracking-wide">Binyuma TV</p>
              <p className="text-[11px] text-gray-500 uppercase tracking-widest">Ads</p>
            </div>
          </Link>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-[#D72638]/15 text-[#D72638]'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                <span>{item.label}</span>
              </NavLink>
            )
          })}
        </nav>

        <div className="px-4 py-4 border-t border-gray-900 space-y-2 text-xs text-gray-400">
          {user && (
            <div className="flex items-center justify-between gap-2">
              <div className="truncate">
                <p className="text-[11px] uppercase tracking-widest text-gray-500">Signed in as</p>
                <p className="text-sm truncate">{user.email}</p>
              </div>
              <span className="inline-flex items-center rounded-full bg-[#D72638]/10 px-2 py-0.5 text-[11px] font-medium text-[#D72638]">
                Ads
              </span>
            </div>
          )}
          <Button
            variant="secondary"
            size="sm"
            className="w-full border-gray-800 text-gray-300 hover:bg-white/5"
            onClick={() => logout()}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign out
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col">
        {/* Mobile header */}
        <header className="md:hidden px-4 py-3 border-b border-gray-900 flex items-center justify-between bg-black/80 backdrop-blur">
          <Link to="/" className="flex items-center gap-2">
            <img src="/img/logo.png" alt="Binyuma TV" className="w-7 h-7 object-contain" />
            <span className="text-sm font-semibold tracking-wide">Binyuma Ads</span>
          </Link>
          <span className="inline-flex items-center gap-1 rounded-full bg-[#D72638]/10 px-2 py-0.5 text-[11px] font-medium text-[#D72638]">
            <Megaphone className="w-3 h-3" />
            Ads
          </span>
        </header>

        <main className="flex-1 bg-gradient-to-b from-black via-[#050309] to-black">
          <div className="max-w-6xl mx-auto px-4 py-6 md:px-8 md:py-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
